<template>
  <section class="pillar" :aria-labelledby="headingId">
    <h2 :id="headingId" class="pillar-title">
      <span class="pillar-icon" aria-hidden="true">{{ pillar.icon }}</span>
      {{ pillar.title }}
    </h2>
    <p class="pillar-subtitle">{{ pillar.subtitle }}</p>
    <!-- Compteur **dérivé**. C'était un littéral (« 3/5 modules complétés ») affiché même quand le
         pilier ne portait aucune activité. -->
    <p class="pillar-counter">{{ counterLabel }}</p>

    <p v-if="cards.length === 0" class="pillar-empty">{{ pillar.emptyLabel }}</p>

    <div v-else class="cards-grid">
      <!-- `article` et non `div` cliquable : la carte porte plusieurs actions (s'inscrire, répondre
           à chaque questionnaire). Les emboîter dans un bouton unique en rendrait une seule
           atteignable, et un `div @click` n'est ni focusable ni activable au clavier. -->
      <article v-for="card in cards" :key="cardKey(card)" class="activity-card">
        <header class="activity-header">
          <!-- Le titre est le point d'entrée du module. C'est un lien et non un `div @click` :
               lui seul est atteignable au clavier et annoncé comme une destination. La carte
               garde ses autres actions à côté, elles ne s'emboîtent pas dedans. -->
          <h3 class="activity-title">
            <router-link class="activity-title-link" :to="moduleRoute(card)">
              <!-- L'icône d'une carte est un nom Material Design (`mdi-web`), pas un emoji comme
                   celle du pilier : l'interpoler en texte affichait « mdi-web » à côté du titre. -->
              <v-icon v-if="isIconName(card.icon)" aria-hidden="true" small>{{ card.icon }}</v-icon>
              <span v-else-if="card.icon" aria-hidden="true">{{ card.icon }}</span>
              {{ card.title }}
            </router-link>
          </h3>
          <!-- L'état est un **texte**. Il était porté par la seule icône de la carte, donc absent
               pour un lecteur d'écran et indistinguable pour qui ne perçoit pas la couleur. -->
          <span class="activity-badges">
            <!-- Les points sont la monnaie de l'écran : ils se lisent d'un coup d'œil, comme sur
                 la maquette, au lieu d'être noyés en fin de ligne de méta. -->
            <span class="activity-points">{{ card.points }} points</span>
            <span class="activity-state" :class="stateClass(card)">{{ card.stateLabel }}</span>
          </span>
        </header>

        <p class="activity-meta">{{ card.kindLabel }} · {{ card.recurrenceLabel }}</p>
        <p v-if="card.description" class="activity-description">{{ card.description }}</p>
        <p class="activity-canteen">{{ card.canteen.name }}</p>

        <p class="activity-progress-label">{{ card.progress }} % réalisé</p>
        <div
          class="activity-track"
          role="progressbar"
          :aria-valuenow="card.progress"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div class="activity-fill" :style="{ width: card.progress + '%' }"></div>
        </div>

        <!-- Les conditions manquantes, en clair : « verrouillée » sans dire à quelle condition
             n'apprend rien. -->
        <ul v-if="card.lockLabels && card.lockLabels.length" class="activity-locks">
          <li v-for="(label, index) in card.lockLabels" :key="index">{{ label }}</li>
        </ul>

        <div class="activity-actions">
          <!-- Le bouton n'est jamais désactivé : un bouton grisé sort du parcours clavier sans rien
               annoncer. Quand l'inscription n'a pas de sens, il n'est pas rendu — et l'état de la
               carte dit pourquoi. -->
          <button
            v-if="card.canAccept"
            type="button"
            class="activity-button"
            :aria-busy="acceptingActionId === card.actionId ? 'true' : 'false'"
            @click="$emit('accept', card.actionId)"
          >
            {{ acceptingActionId === card.actionId ? "Inscription en cours…" : "Participer" }}
          </button>

          <a
            v-for="survey in answerableSurveys(card)"
            :key="survey.id"
            class="activity-link"
            :href="answerUrl(card, survey)"
          >
            Répondre : {{ survey.name }}
          </a>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import { surveyAnswerUrl } from "@/utils"
// Une classe par état, pour la couleur **en plus** du libellé. Aucune information n'y est portée
// qui ne soit déjà dans `stateLabel` : la couleur seule ne restitue rien (RGAA 3.1).
const STATE_CLASSES = {
  SUGGESTED: "state-blue",
  LOCKED: "state-grey",
  ENGAGED: "state-blue",
  DONE_FOR_PERIOD: "state-green",
  COMPLETED: "state-green",
  PASSED: "state-green",
  FAILED: "state-red",
  CLOSED: "state-grey",
}

/**
 * Un pilier de l'écran d'apprentissage et ses cartes.
 *
 * Recevait auparavant un en-tête, un texte de complétion et une liste de cartes déjà mises en forme
 * par l'appelant, dont deux piliers entièrement écrits en dur. Il reçoit désormais le pilier tel
 * que le registre le restitue : son libellé d'état vient du référentiel, il n'en déduit rien.
 */
export default {
  name: "PillarSection",

  props: {
    pillar: {
      type: Object,
      required: true,
    },
    // Activité dont l'inscription est en cours, `null` sinon.
    acceptingActionId: {
      type: Number,
      default: null,
    },
  },

  computed: {
    cards() {
      return this.pillar.cards || []
    },

    headingId() {
      return `pillar-${this.pillar.code.toLowerCase()}`
    },

    counterLabel() {
      if (this.pillar.totalCount === 0) return "Aucune activité proposée"
      return `${this.pillar.doneCount} activité(s) faite(s) sur ${this.pillar.totalCount}`
    },
  },

  methods: {
    /**
     * Une carte de cours n'a pas d'`actionId` — elle en regroupe plusieurs. La clé de boucle
     * distingue donc les deux origines : sans cela, deux cours voisins partageraient la clé
     * `undefined` et Vue réutiliserait le DOM de l'un pour l'autre.
     */
    cardKey(card) {
      return card.actionId ? `action-${card.actionId}` : `course-${card.courseId}`
    },

    /**
     * Où la carte se lit en entier : le sommaire pour un cours, la page du module pour une
     * activité isolée ou une partie ouverte hors de son cours.
     */
    moduleRoute(card) {
      if (!card.actionId && card.courseId) {
        return { name: "CoursePage", params: { courseId: String(card.courseId) } }
      }
      return { name: "PedagogicalModulePage", params: { actionId: String(card.actionId) } }
    },

    /**
     * Les deux formes cohabitent : les activités et les cours portent un nom `mdi-*`, les seeds
     * plus anciennes un emoji. Rendre l'un comme l'autre évite d'afficher un identifiant brut.
     */
    isIconName(icon) {
      return typeof icon === "string" && icon.startsWith("mdi-")
    },

    stateClass(card) {
      return STATE_CLASSES[card.state] || "state-grey"
    },

    /**
     * Questionnaires qu'il reste à renseigner, et seulement si l'activité est engagée : proposer de
     * répondre à ce qu'on n'a pas accepté contournerait l'inscription.
     */
    answerableSurveys(card) {
      if (!card.isEngaged) return []
      return (card.surveys || []).filter((survey) => survey.canAnswer)
    },

    /**
     * Le contexte est dans l'URL de réponse : sans la cantine, l'expérience et **l'activité**, la
     * réponse enregistrée ne dirait pas d'où elle vient, et la restitution par activité ne la
     * retrouverait pas.
     */
    answerUrl(card, survey) {
      return surveyAnswerUrl(survey, {
        canteenId: card.canteen && card.canteen.id,
        experienceId: card.eatingExperienceId,
        actionId: card.actionId,
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import "../../scss/nutriquest";

@import "../../scss/dashboard-layout";

.pillar {
  background: $nq-surface;
  border: 1px solid $nq-border;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(108, 77, 224, 0.1);
  padding: 1.25rem;
}

.pillar-title {
  align-items: center;
  color: $nq-ink;
  display: flex;
  gap: 0.625rem;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
}

/* La pastille dégradée de la maquette : l'icône du pilier y est posée, pas juxtaposée au titre. */
.pillar-icon {
  align-items: center;
  background: $nq-icon-gradient;
  border-radius: 12px;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 1.125rem;
  height: 2.25rem;
  justify-content: center;
  width: 2.25rem;
}

.pillar-subtitle {
  color: $nq-ink-soft;
  margin: 0;
}

.pillar-counter {
  @include nq-pill;
  background: $nq-accent-soft;
  color: $nq-accent-ink;
  display: inline-block;
  margin: 0.5rem 0 1rem 0;
}

.pillar-empty {
  color: $nq-ink-soft;
  font-style: italic;
  margin: 0;
}

.activity-card {
  background: $nq-surface-soft;
  border: 1px solid $nq-border;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  padding: 1rem;
}

.activity-header {
  align-items: flex-start;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
}

.activity-title {
  color: $nq-ink;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

.activity-badges {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  justify-content: flex-end;
}

.activity-state {
  @include nq-pill;
}

/* L'or de la maquette. Le texte est presque noir : blanc sur or ne tient pas le contraste. */
.activity-points {
  @include nq-pill;
  background: $nq-gold;
  color: $nq-gold-ink;
}

.state-blue {
  background: $nq-state-info-bg;
  color: $nq-state-info-ink;
}

.state-green {
  background: $nq-state-done-bg;
  color: $nq-state-done-ink;
}

.state-red {
  background: $nq-state-alert-bg;
  color: $nq-state-alert-ink;
}

.state-grey {
  background: $nq-state-locked-bg;
  color: $nq-state-locked-ink;
}

.activity-meta,
.activity-canteen {
  color: $nq-muted;
  font-size: 0.8125rem;
  margin: 0.375rem 0 0 0;
}

.activity-description {
  color: $nq-ink-soft;
  font-size: 0.875rem;
  margin: 0.5rem 0 0 0;
}

.activity-progress-label {
  color: $nq-ink-soft;
  font-size: 0.8125rem;
  margin: 0.75rem 0 0.25rem 0;
}

.activity-track {
  background: $nq-track;
  border-radius: 999px;
  height: 0.5rem;
  overflow: hidden;
}

.activity-fill {
  background: $nq-fill;
  height: 100%;
}

.activity-locks {
  color: $nq-ink-soft;
  font-size: 0.8125rem;
  margin: 0.5rem 0 0 0;
  padding-left: 1.25rem;
}

/* `flex-start` : un bouton étiré sur toute la largeur de la carte se lit comme une bannière et non
   comme une action, et la cible de clic déborde alors le libellé. */
.activity-actions {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.activity-button {
  @include nq-button;
}

.activity-button:focus-visible,
.activity-link:focus-visible {
  @include nq-focus-ring;
}

.activity-link {
  color: $nq-accent;
  font-size: 0.875rem;
  font-weight: 600;
}

.activity-title-link {
  color: inherit;
  text-decoration: none;
}

.activity-title-link:hover,
.activity-title-link:focus-visible {
  text-decoration: underline;
}

.activity-title-link:focus-visible {
  @include nq-focus-ring;
}
</style>
